import React, { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { Compass, Target, Users, Zap } from "lucide-react";
import { fadeUp, staggerContainer, staggerItem } from "@/lib/motion";
import Tilt from "@/components/landing/effects/Tilt";

const pillars = [
    {
        icon: Compass,
        title: "Built in Prayagraj",
        desc: "Founded by Tarun Sharma & Mohammad Aquib — engineers who deeply understand the Indian aspirant.",
    },
    {
        icon: Target,
        title: "Outcome-Obsessed",
        desc: "Every product line is judged on real user outcomes — pass percentage, hours saved, revenue lifted.",
    },
    {
        icon: Users,
        title: "Senior-Only Team",
        desc: "No outsourcing, no juniors on production. The people who pitch are the people who ship.",
    },
    {
        icon: Zap,
        title: "AI as a First-Class Citizen",
        desc: "Every product ships with LLMs, RAG or ML baked into the core workflow — not as marketing fluff.",
    },
];

const About = () => {
    const ref = useRef(null);
    const { scrollYProgress } = useScroll({
        target: ref,
        offset: ["start end", "end start"],
    });
    const headingY = useTransform(scrollYProgress, [0, 1], [80, -80]);

    return (
        <section
            ref={ref}
            id="about"
            data-testid="about-section"
            className="section py-24 md:py-32"
        >
            <div className="absolute inset-x-0 -top-32 h-64 bg-gradient-to-b from-transparent to-black/40 pointer-events-none" />
            <motion.div
                className="orb"
                style={{
                    width: 480,
                    height: 480,
                    top: 80,
                    left: "-200px",
                    background:
                        "radial-gradient(circle, rgba(0,229,255,0.20), transparent 60%)",
                }}
                animate={{ scale: [1, 1.2, 1], y: [0, 40, 0] }}
                transition={{ duration: 14, repeat: Infinity, ease: "easeInOut" }}
            />

            <div className="max-w-7xl mx-auto px-6 lg:px-10 relative z-10">
                <motion.div
                    variants={staggerContainer}
                    initial="initial"
                    whileInView="whileInView"
                    viewport={{ once: true, margin: "-80px" }}
                    className="grid lg:grid-cols-12 gap-12 lg:gap-16 items-start"
                >
                    <motion.div
                        style={{ y: headingY }}
                        className="lg:col-span-5 lg:sticky lg:top-28"
                    >
                        <motion.span
                            variants={staggerItem}
                            className="font-mono text-xs uppercase tracking-[0.3em] text-cyan-300"
                        >
                            01 / About Us
                        </motion.span>
                        <motion.h2
                            variants={staggerItem}
                            data-testid="about-heading"
                            className="mt-4 font-display font-semibold text-white text-4xl md:text-5xl lg:text-6xl leading-[1.05] tracking-tight"
                        >
                            A product studio
                            <br />
                            from the{" "}
                            <span className="text-grad-cyan">heart of UP.</span>
                        </motion.h2>
                        <motion.p
                            variants={staggerItem}
                            className="mt-6 text-zinc-400 text-lg leading-relaxed max-w-xl"
                        >
                            Atvicon Technologies started in 2022 in Colonel Ganj, Prayagraj
                            with one belief — Indian students and Indian businesses deserve
                            software built for them, not awkwardly localised from Silicon
                            Valley.
                        </motion.p>
                        <motion.p
                            variants={staggerItem}
                            className="mt-4 text-zinc-400 text-lg leading-relaxed max-w-xl"
                        >
                            Today we run three product lines used across 23+ states and
                            partner with founders to ship full-stack platforms, mobile apps
                            and AI-driven marketing automation.
                        </motion.p>
                    </motion.div>

                    <div className="lg:col-span-7 grid sm:grid-cols-2 gap-5">
                        {pillars.map((p, i) => {
                            const Icon = p.icon;
                            return (
                                <motion.div
                                    key={p.title}
                                    {...fadeUp}
                                    transition={{ ...fadeUp.transition, delay: i * 0.1 }}
                                >
                                    <Tilt max={6} scale={1.02}>
                                        <div
                                            data-testid={`about-pillar-${i}`}
                                            className="glass glass-hover rounded-2xl p-7 group h-full"
                                        >
                                            <motion.div
                                                whileHover={{ rotate: 15 }}
                                                className="h-11 w-11 rounded-lg bg-gradient-to-br from-cyan-400/20 to-violet-500/20 border border-white/10 grid place-items-center group-hover:from-cyan-400/40 group-hover:to-violet-500/40 transition"
                                            >
                                                <Icon
                                                    className="h-5 w-5 text-cyan-300"
                                                    strokeWidth={1.75}
                                                />
                                            </motion.div>
                                            <h3 className="mt-5 font-display font-semibold text-white text-xl">
                                                {p.title}
                                            </h3>
                                            <p className="mt-2 text-zinc-400 text-sm leading-relaxed">
                                                {p.desc}
                                            </p>
                                        </div>
                                    </Tilt>
                                </motion.div>
                            );
                        })}
                    </div>
                </motion.div>
            </div>
        </section>
    );
};

export default About;
